'use client';

import * as React from 'react';
import { Loader2, Phone } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import { brandingConfig } from '@/config/branding';
import { QuickStartDemoCard } from '@/components/setup/quick-start/QuickStartDemoCard';
import { QuickStartScorePanel } from '@/components/setup/quick-start/QuickStartScorePanel';
import { QuickStartStoreSelect } from '@/components/setup/quick-start/QuickStartStoreSelect';
import { QuickStartTierSection } from '@/components/setup/quick-start/QuickStartTierSection';
import { groupTasksByTier } from '@/components/setup/quick-start/quickStartFilters.utils';
import { QUICK_START_RECOMMENDED_POINTS } from '@/components/setup/quick-start/quickStartHub.utils';
import { Button } from '@/components/ui/button';
import { PageHeader } from '@/components/ui/page-header';
import { useOrgNavigate } from '@/hooks/use-app-navigate';
import { useEffectiveBranding } from '@/hooks/use-effective-branding';
import { useAccountManagerInfo } from '@/lib/api/account';
import { useQuickStartChecklist } from '@/lib/api/setup';
import { useStoresList } from '@/lib/api/stores';
import { config } from '@/lib/config';

export function QuickStartHub() {
  const { t, i18n } = useTranslation('setup');
  const { orgSlug, orgRoutes } = useOrgNavigate();
  const brand = useEffectiveBranding();
  const isTagadapayBrand = brand.appName === 'TagadaPay';
  const { data: accountManager } = useAccountManagerInfo();
  const { data: storesData } = useStoresList({ pagination: { page: 1, pageSize: 100 } });
  const stores = storesData?.stores ?? [];
  const [selectedStoreId, setSelectedStoreId] = React.useState<string | undefined>(undefined);
  const activeStoreId = selectedStoreId ?? stores[0]?.id;

  const { data, isLoading, isError, refetch, isFetching } = useQuickStartChecklist(activeStoreId);

  const grouped = React.useMemo(() => groupTasksByTier(data?.tasks ?? []), [data?.tasks]);
  const activeTaskId = React.useMemo(
    () =>
      (data?.tasks ?? []).find((task) => task.tier === 'required' && task.status === 'pending')?.id ?? null,
    [data?.tasks],
  );

  // Learn-more links target the beginner-friendly Gleap help center (not the
  // developer docs). Gleap resolves articles by their numeric id; the `/en|/fr`
  // prefix selects the language and the slug is cosmetic. Hidden entirely when
  // no help center URL is configured (e.g. white-label partner hosts).
  const helpLang = i18n.language?.toLowerCase().startsWith('fr') ? 'fr' : 'en';
  const helpBase =
    isTagadapayBrand && brandingConfig.helpCenterUrl
      ? `${brandingConfig.helpCenterUrl.replace(/\/$/, '')}/${helpLang}`
      : '';
  const helpUrl = brandingConfig.helpCenterUrl ?? orgRoutes.ACCOUNT_MANAGER();

  if (isLoading && !data) {
    return (
      <section className="w-full pb-16">
        <div className="flex min-h-[200px] items-center justify-center">
          <Loader2 className="text-muted-foreground size-7 animate-spin" />
        </div>
      </section>
    );
  }

  if (isError || !data) {
    return (
      <section className="w-full pb-16">
        <PageHeader title={t('quickStart.title')} description={t('quickStart.subtitle')} />
        <div className="border-destructive/30 bg-destructive/5 mt-6 rounded-lg border p-5">
          <p className="text-destructive text-sm">{t('quickStart.error')}</p>
          <Button variant="outline" size="sm" className="mt-3" onClick={() => refetch()}>
            {t('quickStart.retry')}
          </Button>
        </div>
      </section>
    );
  }

  const resolvedStoreId = activeStoreId ?? data.storeId;

  const tagadaBookCallUrl = isTagadapayBrand
    ? (accountManager?.callUrl ?? config.introCallUrl)
    : undefined;
  const bookCallUrl = brand.bookCallUrl ?? tagadaBookCallUrl;
  const bookCallLabel =
    brand.bookCallUrl || accountManager?.callUrl
      ? t('bookACall', { ns: 'navigation' })
      : t('bookIntroCall', { ns: 'navigation' });

  return (
    <section className="w-full pb-16">
      <PageHeader title={t('quickStart.title')} description={t('quickStart.subtitle')}>
        {bookCallUrl ? (
          <Button variant="outline" size="sm" asChild className="h-9">
            <a href={bookCallUrl} target="_blank" rel="noopener noreferrer">
              <Phone />
              {bookCallLabel}
            </a>
          </Button>
        ) : null}
        {isTagadapayBrand ? (
          <Button variant="outline" size="sm" asChild className="h-9">
            <Link to={helpUrl} target={helpUrl.startsWith('http') ? '_blank' : undefined}>
              {t('quickStart.help.cta')}
            </Link>
          </Button>
        ) : null}
      </PageHeader>

      <div className="mt-6">
        <QuickStartStoreSelect
          stores={stores}
          selectedStoreId={activeStoreId}
          onStoreChange={setSelectedStoreId}
          isRefreshing={isFetching}
        />
      </div>

      {/* lg:items-start keeps the score panel at its natural height (top-aligned)
          and in normal flow — it scrolls with the page rather than pinning. */}
      <div className="mt-6 grid gap-6 lg:grid-cols-[300px_minmax(0,1fr)] lg:items-start lg:gap-8">
        <div className="space-y-4">
          <QuickStartScorePanel progress={data.progress} />
          <QuickStartDemoCard />
        </div>

        <div className="min-w-0 space-y-4">
          <QuickStartTierSection
            tier="required"
            tasks={grouped.required}
            activeTaskId={activeTaskId}
            orgSlug={orgSlug ?? ''}
            storeId={resolvedStoreId}
            helpBase={helpBase}
          />
          <QuickStartTierSection
            tier="recommended"
            tasks={grouped.recommended}
            activeTaskId={null}
            subtitle={t('quickStart.sections.recommended.pathSubtitle', {
              points: QUICK_START_RECOMMENDED_POINTS,
            })}
            orgSlug={orgSlug ?? ''}
            storeId={resolvedStoreId}
            helpBase={helpBase}
          />
          <QuickStartTierSection
            tier="optional"
            tasks={grouped.optional}
            activeTaskId={null}
            subtitle={t('quickStart.sections.optional.pathSubtitle')}
            orgSlug={orgSlug ?? ''}
            storeId={resolvedStoreId}
            helpBase={helpBase}
          />
        </div>
      </div>
    </section>
  );
}
